import { addDays, isAfter, isBefore, isToday, parse, startOfDay } from "date-fns";
import luhn from "luhn";
import { useText } from "../hooks/lang";
import { texts } from "../lang/translations";
import type {
	CountryAndTinAnswer,
	FieldValue,
	LangKey,
	SimpleFieldValue,
	ValidatorFn,
} from "../types";
import type { SniCode } from "../types/sni";
import { isValidDate } from "./date";
import { ibanLengths } from "./iban-length";
import { isValidPaymentAmount, normalizeSsn } from "./numbers";

export const composeValidators =
	<T extends FieldValue>(...validators: Array<ValidatorFn<T>>) =>
	(value: T) =>
		validators.reduce(
			(error: LangKey | string | undefined, validator) => error || validator(value),
			undefined,
		);

export const validateEmail = (email: string) => {
	const emailRegex =
		/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	if (email && !emailRegex.test(email)) {
		return texts.validation.emailFormatError;
	}
	return undefined;
};

export const validatePhoneNumber = (phoneNumber: string) => {
	const phoneNumberRegex = /^[0]{0,1}[1-9][0-9]{6,20}$/;
	if (!phoneNumberRegex.test(phoneNumber)) {
		return texts.validation.phoneNumberFormatError;
	}
	return undefined;
};

export const cardPhoneNumberRegex = /^[0]{0,1}[1-9][0-9]{6,16}$/;
// Stricter phone validation based on the format used in 3Dsecure process
export const validateCardPhoneNumber = (phoneNumber: string) => {
	if (!cardPhoneNumberRegex.test(phoneNumber)) {
		return texts.validation.phoneNumberFormatError;
	}
	return undefined;
};

export const validateSwishNumber = (swishNumber: string) => {
	if (!swishNumber) return texts.validation.missingSwishNumber;

	const numberWithoutSpaces = swishNumber.replace(/\s+/g, "");
	return /^123[0-6][0-9]{6}$/.test(numberWithoutSpaces) && luhn.validate(numberWithoutSpaces)
		? undefined
		: texts.validation.invalidSwishNumber;
};

export const validateSwishCPOCPhoneNumber = (phoneNumber: string) => {
	const phoneNumberRegex = /^7[0-9]{8}$/;
	if (!phoneNumberRegex.test(phoneNumber)) {
		return texts.validation.phoneNumberFormatError;
	}
	return undefined;
};

export const isOnlyNumeric = (value: SimpleFieldValue) =>
	value !== null && value !== undefined && /^\d+$/.test(value.toString())
		? undefined
		: texts.validation.onlyNumbersAllowed;

export const isRequired = (value: FieldValue, errorMsg: LangKey = texts.validation.mandatory) => {
	if (value === null || value === undefined) return errorMsg;

	if (Array.isArray(value)) {
		return value.length > 0 && value.every((v) => v.length > 0) ? undefined : errorMsg;
	}

	if (typeof value === "string") {
		return value.trim() ? undefined : errorMsg;
	}
	if (typeof value === "boolean") {
		return value ? undefined : errorMsg;
	}

	return undefined;
};

export const isRequiredEmail = (value: string) =>
	isRequired(value, texts.validation.mandatoryEmail);

export const isRequiredPhone = (value: string) =>
	isRequired(value, texts.validation.mandatoryPhone);

export const validateCountryAndTin = (values: Array<CountryAndTinAnswer> = []) =>
	isRequired(values.map((v) => v.answerValue.tin));

export const validateSsn = (ssn: string) => {
	const errorMsg = texts.validation.invalidSsn;

	const regex = /^(19|20)[0-9]{6}[-]?[0-9]{4}$/;

	if (!regex.test(ssn)) return errorMsg;

	const formattedSsn = normalizeSsn(ssn).slice(2, 12);

	if (luhn.validate(formattedSsn)) {
		return undefined;
	}

	return errorMsg;
};

// TODO: Merge with validateSSN somehow? Only difference is error message
export const validateSsnCPOC = (ssn: string) => {
	const errorMsg = texts.validation.invalidSsnCPOC;

	const regex = /^(19|20)[0-9]{6}[-]?[0-9]{4}$/;

	if (!regex.test(ssn)) return errorMsg;

	const formattedSsn = normalizeSsn(ssn).slice(2, 12);

	if (luhn.validate(formattedSsn)) {
		return undefined;
	}

	return errorMsg;
};

export const validateMin = (min: number, errorMsg: string) => (value: SimpleFieldValue) =>
	Number(value) >= min ? undefined : errorMsg;

export const validateMax = (max: number, errorMsg: string) => (value: SimpleFieldValue) =>
	Number(value) <= max ? undefined : errorMsg;

export const validateStep = (step: number) => (value: string) =>
	!(Number(value) % step) ? undefined : texts.validation.evenThousand;

export const validateSniCode = (allCodes: Array<SniCode>) => (code: string) =>
	/^\d{2}[.]?\d{3}$/.test(code) && allCodes.some((c) => c.sniCode === code.replace(/\D/g, ""))
		? undefined
		: texts.validation.invalidSniCode;

export const validatePastOrTodayDate = (dateStr: string) => {
	if (!isValidDate(dateStr)) return texts.validation.invalidDate;

	const parsedDate = parse(dateStr, "yyyy-MM-dd", new Date());

	const todayDate = startOfDay(new Date());

	if (isBefore(parsedDate, todayDate) || isToday(parsedDate)) return undefined;

	return texts.validation.invalidDate;
};

export const validatePaymentDate = (dateStr: string | undefined) => {
	if (!dateStr) return undefined; // ASAP date

	if (!isValidDate(dateStr)) return texts.validation.invalidDate;

	const todayDate = startOfDay(new Date());
	const maxDate = addDays(todayDate, 366);

	if (isToday(dateStr) || (isAfter(dateStr, todayDate) && !isAfter(dateStr, maxDate)))
		return undefined;

	return texts.validation.invalidDate;
};

export const validateMaxLength = (max: number) => (value?: string | null) => {
	const [t] = useText();
	const v = value?.toString();
	return v && v.length > max ? t(texts.validation.maxLength, max.toString()) : undefined;
};

export const validateZipCode = (zipCode: string) => {
	const zipCodeRegex = /^[0-9]{5}$/;
	if (!zipCodeRegex.test(zipCode)) {
		return texts.validation.invalidZipCode;
	}
	return undefined;
};

// should probably be used for all zip codes:
export const validateClcZipCode = (zipCode: string) => {
	const zipCodeRegex = /^[0-9]{3}[ ]{0,1}[0-9]{2}$/;
	if (!zipCodeRegex.test(zipCode)) {
		return texts.validation.invalidZipCode;
	}
	return undefined;
};

export const validateClcCompanyName = (companyName: string) =>
	companyName.match(/^[A-Za-zåäöÅÄÖ0-9.,?:/()&+-\\ ]+$/) &&
	companyName.match(/( AB$)|(AB )|(aktiebolag)|(Aktiebolag)/)
		? undefined
		: texts.validation.invalidCompanyName;

export const validatePaymentAmount = (value: string) => {
	if (isValidPaymentAmount(value)) return undefined;
	return texts.validation.invalidPaymentAmount;
};

export const validateUniqueString =
	(strings: Array<string>, errorMsg: string | LangKey) => (value: string) =>
		strings.includes(value) ? errorMsg : undefined;

export function validateIBAN(iban: string) {
	// Remove spaces and make uppercase
	const cleanIban = iban.replace(/\s+/g, "").toUpperCase();

	// Must be at least 4 characters (country code + check digits)
	if (cleanIban.length < 4) {
		return texts.validation.invalidIban;
	}

	const countryCode = cleanIban.slice(0, 2);

	// Validate length against country-specific IBAN lengths
	const expectedLength = ibanLengths[countryCode];
	if (!expectedLength || cleanIban.length !== expectedLength) {
		return texts.validation.invalidIban;
	}

	// Move first 4 characters to the end of the string
	const rearranged = cleanIban.slice(4) + cleanIban.slice(0, 4);

	// Convert letters to numbers (A=10, B=11, ..., Z=35)
	const numericIban = rearranged
		.split("")
		.map((ch) => {
			if (/[A-Z]/.test(ch)) {
				return (ch.charCodeAt(0) - 55).toString(); // 'A' (65) -> 10
			}
			return ch;
		})
		.join("");

	// Perform mod-97 operation in chunks to avoid big number overflow
	let remainder = "";
	for (let i = 0; i < numericIban.length; i += 7) {
		const block = remainder + numericIban.substr(i, 7);
		remainder = (Number.parseInt(block, 10) % 97).toString();
	}

	return Number.parseInt(remainder, 10) === 1 ? undefined : texts.validation.invalidIban;
}

// Validate that a clearing number is xxxx, xxxxx or xxxx-x
export const validateClearingNumber = (value: string) => {
	const clearingNumberRegEx = /^([0-9]{4})(-)?([0-9])?$/;
	if (clearingNumberRegEx.test(value)) {
		return undefined;
	}
	return texts.validation.invalidClearingNumber;
};

// 1234-1234 or 123-1234
export const isBankgiroFormat = (value: string) => /^[0-9]{3,4}(-?)[0-9]{4}$/.test(value);

// 1234567-8
export const isPlusgiroFormat = (value: string) => /^[0-9]{1,7}(-?)[0-9]{1}$/.test(value);

// Will accept BG or PG format (Also checks luhn checksum)
export const validateGiroFormat = (value: string) => {
	if (isBankgiroFormat(value) || isPlusgiroFormat(value)) {
		const parsedGiro = value.replace("-", "");
		if (luhn.validate(parsedGiro)) {
			return undefined;
		}
	}
	return texts.validation.giroFormatError;
};

export const validateAlphaNumeric = (value: string) => {
	if (/^[ a-zA-Z0-9åäöÅÄÖ,.-]+$/.test(value) || value === "") {
		return undefined;
	}
	return texts.validation.onlyCertainCharactersAllowed;
};
